import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, AlertTriangle, CheckCircle, Clock, Eye, Trash2, RefreshCw, Filter, X } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const severityConfig = {
  critical: { label: 'Critical', bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300',    dot: 'bg-red-500'    },
  high:     { label: 'High',     bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', dot: 'bg-orange-500' },
  medium:   { label: 'Medium',   bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', dot: 'bg-yellow-500' },
  low:      { label: 'Low',      bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   dot: 'bg-blue-500'   },
};

function SeverityBadge({ severity }) {
  const cfg = severityConfig[severity] || severityConfig.low;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} ${cfg.border} border`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '�';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AlertRow({ alert, onMarkRead }) {
  const [expanded, setExpanded] = useState(false);
  const isRead = !!alert.readAt;

  return (
    <div className={`border rounded-lg transition-colors ${isRead ? 'border-slate-200 bg-white' : 'border-orange-200 bg-orange-50'}`}>
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 mt-1">
          {isRead
            ? <CheckCircle className="h-4 w-4 text-slate-400" />
            : <AlertTriangle className="h-4 w-4 text-orange-500" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-sm font-semibold ${isRead ? 'text-slate-700' : 'text-slate-900'}`}>
              {alert.eventType}
            </span>
            <SeverityBadge severity={alert.severity} />
            {!isRead && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">
                Unread
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(alert.createdAt)}
            </span>
            {alert.ipAddress && (
              <span>IP: <code className="font-mono">{alert.ipAddress}</code></span>
            )}
            {alert.userEmail && (
              <span>User: <span className="font-medium text-slate-700">{alert.userEmail}</span></span>
            )}
          </div>

          {isRead && (
            <p className="text-xs text-slate-400">
              Read {formatDate(alert.readAt)}{alert.readBy ? ` by ${alert.readBy}` : ''}
            </p>
          )}

          {alert.details && Object.keys(alert.details).length > 0 && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Eye className="h-3 w-3" />
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}

          {expanded && alert.details && (
            <pre className="mt-2 p-3 bg-slate-100 rounded text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(alert.details, null, 2)}
            </pre>
          )}
        </div>

        {!isRead && (
          <button
            onClick={() => onMarkRead(alert.id)}
            className="flex-shrink-0 p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            title="Mark as read"
          >
            <CheckCircle className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

const SecurityAlerts = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [severityFilter, setSeverityFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  const buildParams = () => {
    const p = new URLSearchParams();
    if (severityFilter) p.set('severity', severityFilter);
    if (unreadOnly) p.set('unread_only', 'true');
    return p.toString();
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['securityAlerts', severityFilter, unreadOnly],
    queryFn: async () => {
      const params = buildParams();
      const res = await fetch(`${API_BASE}/admin/security/alerts${params ? '?' + params : ''}`, {
        headers: authHeader,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    refetchInterval: 60000,
  });

  const markReadMutation = useMutation({
    mutationFn: async (alertId) => {
      const res = await fetch(`${API_BASE}/admin/security/alerts/${alertId}/mark-read`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['securityAlerts'] }),
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/admin/security/alerts/clear`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['securityAlerts'] });
      setConfirmClear(false);
    },
  });

  const alerts = data?.alerts || [];
  const unreadCount = alerts.filter(a => !a.readAt).length;

  const counts = alerts.reduce((acc, a) => {
    acc[a.severity] = (acc[a.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-500" />
            Security Alerts
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Monitor and review security events detected by the system
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-md text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {unreadCount > 0 && (
            <button
              onClick={() => setConfirmClear(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Clear All Unread ({unreadCount})
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['critical', 'high', 'medium', 'low'].map(sev => {
          const cfg = severityConfig[sev];
          return (
            <div key={sev} className={`p-3 rounded-lg border ${cfg.bg} ${cfg.border}`}>
              <p className={`text-xs font-medium ${cfg.text}`}>{cfg.label}</p>
              <p className={`text-2xl font-bold ${cfg.text}`}>{counts[sev] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
        <Filter className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-600">Severity:</label>
          <select
            value={severityFilter}
            onChange={e => setSeverityFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={e => setUnreadOnly(e.target.checked)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Unread only
        </label>
        {(severityFilter || unreadOnly) && (
          <button
            onClick={() => { setSeverityFilter(''); setUnreadOnly(false); }}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <X className="h-3 w-3" /> Clear filters
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">
          {alerts.length} alert{alerts.length !== 1 ? 's' : ''} � {unreadCount} unread
        </span>
      </div>

      {/* Error state */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load alerts</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {/* Mutation errors */}
      {(markReadMutation.error || clearMutation.error) && (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>
            {(markReadMutation.error || clearMutation.error)?.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && alerts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Shield className="h-12 w-12 text-green-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-slate-700">No alerts found</h3>
          <p className="text-sm text-slate-500 mt-1">
            {severityFilter || unreadOnly
              ? 'Try adjusting your filters.'
              : 'No security events have been recorded yet.'}
          </p>
        </div>
      )}

      {/* Alert list */}
      {!isLoading && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map(alert => (
            <AlertRow
              key={alert.id}
              alert={alert}
              onMarkRead={(id) => markReadMutation.mutate(id)}
            />
          ))}
        </div>
      )}

      {/* Clear confirmation modal */}
      {confirmClear && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4 w-full">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Mark all as read?</h3>
            <p className="text-sm text-slate-600 mb-5">
              This will mark all {unreadCount} unread alert{unreadCount !== 1 ? 's' : ''} as read.
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmClear(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => clearMutation.mutate()}
                disabled={clearMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {clearMutation.isPending ? 'Clearing�' : 'Clear all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityAlerts;